import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  User, 
  Search, 
  Menu, 
  X,
  Heart,
  Crown
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSiteSettings } from '../../hooks/useSiteSettings';
import LanguageSelector from '../ui/LanguageSelector';
import SearchModal from '../ui/SearchModal';
import StyledLink from '../ui/StyledLink';
import MiniCart from '../ui/MiniCart';
import InlineEditor from '../ui/InlineEditor';

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { getCartItemCount } = useCart();
  const { itemCount: wishlistCount } = useWishlist();
  const { t } = useLanguage();
  const { getNavSetting, getSetting } = useSiteSettings();
  const location = useLocation();
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  const navigation = [
    { name: getNavSetting('home'), href: '/' },
    { name: getNavSetting('products'), href: '/products' },
    { name: getNavSetting('categories'), href: '/categories' },
    { name: getNavSetting('about'), href: '/about' },
    { name: getNavSetting('contact'), href: '/contact' },
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-gray-900/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 xl:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
              <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-yellow-500/25 transition-all duration-300">
                <Crown size={20} className="sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white group-hover:text-yellow-500 transition-colors">
                  <InlineEditor settingKey="header_logo_text" defaultValue="TOLOU">
                    {getSetting('header_logo_text', 'TOLOU')}
                  </InlineEditor>
                </h1>
                <p className="text-xs text-gray-300">
                  <InlineEditor settingKey="header_tagline_fr" defaultValue="Bijoux Élégants">
                    {getSetting('header_tagline_fr', 'Bijoux Élégants')}
                  </InlineEditor>
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-gray-300 hover:text-yellow-500 transition-colors duration-200 font-medium text-lg ${
                    location.pathname === item.href ? 'text-yellow-500' : ''
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Language Selector - Hidden on very small screens */}
              <div className="hidden sm:block">
                <LanguageSelector />
              </div>

              {/* Search Button - Hidden on very small screens */}
              <button
                onClick={() => setIsSearchModalOpen(true)}
                className="hidden sm:block p-2 text-gray-300 hover:text-yellow-500 transition-colors duration-200"
              >
                <Search size={26} />
              </button>

              {/* Wishlist - Hidden on very small screens when not authenticated */}
              {isAuthenticated && (
                <Link
                  to="/wishlist"
                  className="hidden sm:block p-2 text-gray-300 hover:text-yellow-500 transition-colors duration-200 relative"
                >
                  <Heart size={26} />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold">
                      {wishlistCount > 99 ? '99+' : wishlistCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Cart - Always visible but smaller on mobile */}
              <div className="scale-90 sm:scale-100">
                <MiniCart />
              </div>

              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative group">
                  <button className="flex items-center space-x-2 p-1 sm:p-2 text-gray-300 hover:text-yellow-500 transition-colors duration-200">
                    <User size={20} className="sm:w-6 sm:h-6" />
                    <span className="hidden md:block text-sm sm:text-base">
                      {user?.first_name || 'Utilisateur'}
                    </span>
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 top-full mt-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="py-2">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-base text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                      >
                        {t('common.profile')}
                      </Link>
                      <Link
                        to="/orders"
                        className="block px-4 py-2 text-base text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                      >
                        {t('common.orders')}
                      </Link>
                      <Link
                        to="/wishlist"
                        className="block px-4 py-2 text-base text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                      >
                        {t('common.wishlist')}
                      </Link>
                      {user?.role === 'admin' && (
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-base text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                        >
                          {t('common.admin')}
                      </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-base text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                      >
                        {t('common.logout')}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="hidden sm:flex items-center space-x-2 lg:space-x-3">
                  {/* Bouton Connexion stylé - Hidden on very small screens */}
                  <StyledLink 
                    to="/login" 
                    variant="secondary"
                    size="sm"
                    icon={User}
                  >
                    {t('common.login')}
                  </StyledLink>

                  {/* Bouton Inscription stylé - Hidden on very small screens */}
                  <StyledLink 
                    to="/register" 
                    variant="primary"
                    size="sm"
                    icon={Crown}
                  >
                    {t('common.register')}
                  </StyledLink>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-1 sm:p-2 text-gray-300 hover:text-yellow-500 transition-colors duration-200"
              >
                {isMobileMenuOpen ? <X size={24} className="sm:w-8 sm:h-8" /> : <Menu size={24} className="sm:w-8 sm:h-8" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-gray-900 border-t border-gray-700">
            <div className="px-4 py-6 space-y-4">
              {/* Mobile-only navigation items */}
              <div className="space-y-4">
                {/* Language Selector - Mobile version */}
                <div className="sm:hidden">
                  <LanguageSelector />
                </div>

                {/* Search Button - Mobile version */}
                <button
                  onClick={() => {
                    setIsSearchModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="sm:hidden flex items-center space-x-2 text-gray-300 hover:text-yellow-500 transition-colors duration-200 w-full"
                >
                  <Search size={20} />
                  <span>Rechercher</span>
                </button>

                {/* Wishlist - Mobile version for authenticated users */}
                {isAuthenticated && (
                  <Link
                    to="/wishlist"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="sm:hidden flex items-center space-x-2 text-gray-300 hover:text-yellow-500 transition-colors duration-200 relative"
                  >
                    <Heart size={20} />
                    <span>Favoris</span>
                    {wishlistCount > 0 && (
                      <span className="bg-red-500 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                        {wishlistCount > 99 ? '99+' : wishlistCount}
                      </span>
                    )}
                  </Link>
                )}
              </div>

              {/* Navigation Links */}
              <div className="pt-4 border-t border-gray-700 space-y-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block text-lg font-medium ${
                      location.pathname === item.href 
                        ? 'text-yellow-500' 
                        : 'text-gray-300 hover:text-yellow-500'
                    } transition-colors duration-200`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              
              {!isAuthenticated && (
                <div className="pt-4 border-t border-gray-700 space-y-4">
                  {/* Bouton Connexion Mobile stylé */}
                  <StyledLink 
                    to="/login" 
                    variant="secondary"
                    size="lg"
                    icon={User}
                    fullWidth
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('common.login')}
                  </StyledLink>
                  
                  {/* Bouton Inscription Mobile stylé */}
                  <StyledLink 
                    to="/register" 
                    variant="primary"
                    size="lg"
                    icon={Crown}
                    fullWidth
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('common.register')}
                  </StyledLink>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Search Modal */}
      <SearchModal 
        isOpen={isSearchModalOpen} 
        onClose={() => setIsSearchModalOpen(false)} 
      />
    </>
  );
};

export default Header; 